/**
 * Quality Report API Service - RTK Query
 * BuilderBel Construction Project Management System
 */

import { baseApi } from './api';
import { ApiResponse } from '../types';
import type {
  QualityReport,
  QualityReportListParams,
  QualityReportListResponse,
  QualityReportStats,
  CreateQualityReportRequest,
  UpdateQualityReportRequest,
  ReviewQualityReportRequest,
  RejectQualityReportRequest,
} from '../types/qualityReport';

/**
 * Quality Reports API Endpoints
 * Base URL: /projects/:projectId/quality-reports
 */
export const qualityReportApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    // ==========================================================================
    // STATS
    // ==========================================================================

    getQualityReportStats: builder.query<
      ApiResponse<QualityReportStats>,
      { projectId: string }
    >({
      query: ({ projectId }) => ({
        url: `/projects/${projectId}/quality-reports/stats`,
      }),
      providesTags: (_result, _error, { projectId }) => [
        { type: 'QualityReportStats', id: projectId },
      ],
    }),

    // ==========================================================================
    // LIST
    // ==========================================================================

    getQualityReports: builder.query<
      ApiResponse<QualityReportListResponse>,
      { projectId: string; params?: QualityReportListParams }
    >({
      query: ({ projectId, params }) => ({
        url: `/projects/${projectId}/quality-reports`,
        params,
      }),
      providesTags: ['QualityReport'],
    }),

    // ==========================================================================
    // GET SINGLE
    // ==========================================================================

    getQualityReport: builder.query<
      ApiResponse<QualityReport>,
      { projectId: string; reportId: string }
    >({
      query: ({ projectId, reportId }) => ({
        url: `/projects/${projectId}/quality-reports/${reportId}`,
      }),
      providesTags: (_result, _error, { reportId }) => [
        { type: 'QualityReport', id: reportId },
      ],
    }),

    // ==========================================================================
    // CREATE
    // ==========================================================================

    createQualityReport: builder.mutation<
      ApiResponse<QualityReport>,
      { projectId: string; data: CreateQualityReportRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/quality-reports`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['QualityReport', 'QualityReportStats'],
    }),

    // ==========================================================================
    // UPDATE
    // ==========================================================================

    updateQualityReport: builder.mutation<
      ApiResponse<QualityReport>,
      { projectId: string; reportId: string; data: UpdateQualityReportRequest }
    >({
      query: ({ projectId, reportId, data }) => ({
        url: `/projects/${projectId}/quality-reports/${reportId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { reportId }) => [
        'QualityReport',
        'QualityReportStats',
        { type: 'QualityReport', id: reportId },
      ],
    }),

    // ==========================================================================
    // DELETE
    // ==========================================================================

    deleteQualityReport: builder.mutation<
      ApiResponse<void>,
      { projectId: string; reportId: string }
    >({
      query: ({ projectId, reportId }) => ({
        url: `/projects/${projectId}/quality-reports/${reportId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['QualityReport', 'QualityReportStats'],
    }),

    // ==========================================================================
    // WORKFLOW ACTIONS
    // ==========================================================================

    completeQualityReport: builder.mutation<
      ApiResponse<QualityReport>,
      { projectId: string; reportId: string }
    >({
      query: ({ projectId, reportId }) => ({
        url: `/projects/${projectId}/quality-reports/${reportId}/complete`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { reportId }) => [
        'QualityReport',
        'QualityReportStats',
        { type: 'QualityReport', id: reportId },
      ],
    }),

    reviewQualityReport: builder.mutation<
      ApiResponse<QualityReport>,
      { projectId: string; reportId: string; data: ReviewQualityReportRequest }
    >({
      query: ({ projectId, reportId, data }) => ({
        url: `/projects/${projectId}/quality-reports/${reportId}/review`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { reportId }) => [
        'QualityReport',
        'QualityReportStats',
        { type: 'QualityReport', id: reportId },
      ],
    }),

    approveQualityReport: builder.mutation<
      ApiResponse<QualityReport>,
      { projectId: string; reportId: string }
    >({
      query: ({ projectId, reportId }) => ({
        url: `/projects/${projectId}/quality-reports/${reportId}/approve`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { reportId }) => [
        'QualityReport',
        'QualityReportStats',
        { type: 'QualityReport', id: reportId },
      ],
    }),

    rejectQualityReport: builder.mutation<
      ApiResponse<QualityReport>,
      { projectId: string; reportId: string; data: RejectQualityReportRequest }
    >({
      query: ({ projectId, reportId, data }) => ({
        url: `/projects/${projectId}/quality-reports/${reportId}/reject`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { reportId }) => [
        'QualityReport',
        'QualityReportStats',
        { type: 'QualityReport', id: reportId },
      ],
    }),
  }),
});

export const {
  useGetQualityReportStatsQuery,
  useGetQualityReportsQuery,
  useGetQualityReportQuery,
  useCreateQualityReportMutation,
  useUpdateQualityReportMutation,
  useDeleteQualityReportMutation,
  useCompleteQualityReportMutation,
  useReviewQualityReportMutation,
  useApproveQualityReportMutation,
  useRejectQualityReportMutation,
} = qualityReportApi;
